'use client'

import { useMemo, useRef, useCallback } from 'react'
import {
  MicrophoneIcon,
  StopIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react'
import { AnimatedSection } from '@/ui/animated-section'
import { useStreamingVoiceRecorder } from './voice/useStreamingVoiceRecorder'
import { cn } from '@/lib/tailwind'
import type { Project } from '@/document'
import { AnalysisPanel } from './AnalysisPanel'
import {
  FormTitleSection,
  FormContentSection,
  FormChecklistSection,
  FormSubmitSection,
  FormMetadataSection,
  FormMediaSection,
} from './form-sections'
import { useFormState } from './hooks/useFormState'
import { useFormSubmission } from './hooks/useFormSubmission'
import { useFormHandlers } from './hooks/useFormHandlers'
import { useAnalysisApplication } from './hooks/useAnalysisApplication'
import { useFormAnalysis } from './hooks/useFormAnalysis'

interface UnifiedFormProps {
  onSuccess?: () => void
  availableProjects?: Project[]
  /** When set, project is pre-filled and locked (used in project detail view) */
  enforcedProjectId?: string
}

export function UnifiedForm({
  onSuccess,
  availableProjects = [],
  enforcedProjectId,
}: UnifiedFormProps) {
  const formState = useFormState({ enforcedProjectId })
  const submission = useFormSubmission()

  // Track content baseline and accumulated partial text during streaming
  const preRecordingContentRef = useRef('')
  const accumulatedTranscriptRef = useRef('')

  const handlePartialTranscript = useCallback(
    (delta: string) => {
      accumulatedTranscriptRef.current += delta
      const base = preRecordingContentRef.current
      const separator = base.trim() ? ' ' : ''
      formState.setDraftValue(
        'content',
        base + separator + accumulatedTranscriptRef.current,
      )
      if (formState.error) formState.setError(null)
    },
    [formState],
  )

  const handleFinalTranscript = useCallback(
    (transcript: string) => {
      // Replace accumulated deltas with the clean final transcript
      const base = preRecordingContentRef.current
      const separator = base.trim() ? ' ' : ''
      const finalContent = base + separator + transcript
      formState.setDraftValue('content', finalContent)
      // Update baseline for the next speech turn within the same recording session
      preRecordingContentRef.current = finalContent
      accumulatedTranscriptRef.current = ''
    },
    [formState],
  )

  const handleVoiceError = useCallback(
    (error: string) => {
      formState.setError(error)
    },
    [formState],
  )

  const {
    isRecording,
    isConnecting,
    startRecording: rawStartRecording,
    stopRecording,
  } = useStreamingVoiceRecorder({
    onPartialTranscript: handlePartialTranscript,
    onFinalTranscript: handleFinalTranscript,
    onError: handleVoiceError,
  })

  const startRecording = useCallback(async () => {
    preRecordingContentRef.current = formState.content
    accumulatedTranscriptRef.current = ''
    await rawStartRecording()
  }, [formState.content, rawStartRecording])

  const projectsForAnalysis = useMemo(
    () => availableProjects.map((p) => ({ id: p.id, name: p.name })),
    [availableProjects],
  )

  const fa = useFormAnalysis({
    content: formState.content,
    checklistItemsLength: formState.checklistItems?.length ?? 0,
    userSelectedType: formState.userSelectedType,
    isSubmitting: submission.isLoading,
    availableProjects: projectsForAnalysis,
  })

  useAnalysisApplication({
    formState,
    analysis: fa.analysis,
    aiDetectedType: fa.aiDetectedType,
    effectiveType: fa.effectiveType,
    isMediaDetected: fa.isMediaDetected,
    peopleForAnalysis: fa.peopleForAnalysis,
    availableTopics: fa.availableTopics,
    availableProjects: fa.availableProjects,
    enforcedProjectId,
  })

  const selectedPeople = useMemo(
    () =>
      fa.availablePeople.filter((p) =>
        formState.selectedPeopleIds.includes(p.id),
      ),
    [fa.availablePeople, formState.selectedPeopleIds],
  )

  const hasContent =
    formState.content.trim().length > 0 ||
    formState.title.trim().length > 0 ||
    Boolean(formState.checklistItems && formState.checklistItems.length > 0)

  const hasChecklist =
    fa.effectiveType === 'task' && !!formState.checklistItems?.length
  const textareaIsCollapsed = hasChecklist && !formState.content.trim()

  const handlers = useFormHandlers({
    formState,
    submission,
    analysis: fa.analysis,
    aiDetectedType: fa.aiDetectedType,
    isMediaDetected: fa.isMediaDetected,
    enrichedData: fa.enrichedData,
    effectiveType: fa.effectiveType,
    selectedPeople,
    resetAnalysis: fa.resetAnalysis,
    resetEnrichment: fa.resetEnrichment,
    setEnrichedData: fa.setEnrichedData,
    onSuccess,
  })

  const showTitleAndMetadata =
    (!!fa.effectiveType ||
      (fa.isAnalyzing && formState.hasShownTitleRef.current)) &&
    !fa.isMediaDetected

  const handleTypeToggle = () => {
    const current = fa.effectiveType
    const next = current === 'task' ? 'journal' : 'task'
    formState.setUserSelectedType(next)
  }

  return (
    <form onSubmit={handlers.handleSubmit} aria-label="Capture new entry">
      <AnimatedSection show={showTitleAndMetadata}>
        <FormTitleSection
          title={formState.title}
          onTitleChange={(value) => {
            formState.markFieldModified('title')
            formState.setDraftValue('title', value)
          }}
          isAnalyzing={fa.isAnalyzing}
          hasShownTitle={formState.hasShownTitleRef.current}
          isLoading={submission.isLoading}
          effectiveType={fa.effectiveType}
          onTypeToggle={handleTypeToggle}
        />
      </AnimatedSection>

      <FormContentSection
        content={formState.content}
        onContentChange={(value) => formState.setDraftValue('content', value)}
        onClearError={() => {
          if (formState.error) formState.setError(null)
        }}
        isLoading={submission.isLoading}
        textareaIsCollapsed={textareaIsCollapsed}
      />

      <div className="mt-2">
        <AnalysisPanel isAnalyzing={fa.isAnalyzing} hasContent={hasContent} />
      </div>

      <AnimatedSection show={!!formState.error}>
        {formState.error && (
          <p
            className="text-destructive text-sm break-words"
            role="alert"
            aria-live="polite"
          >
            {formState.error}
          </p>
        )}
      </AnimatedSection>

      <FormMediaSection
        formState={formState}
        handlers={handlers}
        hasContent={hasContent}
        isMediaDetected={fa.isMediaDetected}
        aiDetectedType={fa.aiDetectedType}
        isAnalyzing={fa.isAnalyzing}
        enrichedData={fa.enrichedData}
        isEnriching={fa.isEnriching}
        enrichmentError={fa.enrichmentError}
        retryEnrichment={fa.retryEnrichment}
        selectedPeople={selectedPeople}
        availablePeople={fa.availablePeople}
        isPeopleLoading={fa.isPeopleLoading}
        labelSuggestions={fa.labelSuggestions}
        isCheckingLabels={fa.isCheckingLabels}
        toggleLabelSuggestion={fa.toggleLabelSuggestion}
        isLoading={submission.isLoading}
      />

      <AnimatedSection
        show={fa.effectiveType === 'task' && !!formState.checklistItems?.length}
        delay="100ms"
        className="mt-2"
      >
        <FormChecklistSection
          checklistItems={formState.checklistItems}
          onToggle={handlers.handleChecklistToggle}
          isLoading={submission.isLoading}
        />
      </AnimatedSection>

      {/* Bottom toolbar — always visible */}
      <div className="mt-3 flex items-center gap-2">
        {/* Metadata chips — left, takes remaining space */}
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <FormMetadataSection
            show={showTitleAndMetadata}
            formState={formState}
            handlers={handlers}
            effectiveType={fa.effectiveType}
            selectedPeople={selectedPeople}
            availablePeople={fa.availablePeople}
            isPeopleLoading={fa.isPeopleLoading}
            availableTopics={fa.availableTopics}
            availableProjects={availableProjects}
            projectLocked={!!enforcedProjectId}
            isLoading={submission.isLoading}
          />
        </div>

        {/* Right group: mic + Create */}
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={submission.isLoading || isConnecting}
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-all',
              isRecording
                ? 'bg-destructive/10 text-destructive'
                : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground',
              (submission.isLoading || isConnecting) &&
                'cursor-not-allowed opacity-40',
            )}
            aria-label={
              isConnecting
                ? 'Connecting to voice service'
                : isRecording
                  ? 'Stop recording'
                  : 'Start voice input'
            }
            title="Voice input"
          >
            {isConnecting ? (
              <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
            ) : isRecording ? (
              <div className="relative">
                <StopIcon className="h-3.5 w-3.5" weight="fill" />
                <span className="bg-destructive/20 absolute inset-0 animate-ping rounded-full" />
              </div>
            ) : (
              <MicrophoneIcon className="h-3.5 w-3.5" />
            )}
          </button>

          <FormSubmitSection
            isLoading={submission.isLoading}
            hasContent={hasContent}
          />
        </div>
      </div>
    </form>
  )
}
